/**
 * 20260919 - IPHONE DUO SIDE DOCK: "is there a reserved vertical strip, and how wide is it?"
 *
 * The single question the shell asks. On the Duo's outer display the system reserves a strip on the
 * trailing edge for its status bar, camera housing and vertical bar buttons; the app's own quiet
 * controls belong in that strip rather than crowding the top bar beside it (Apple's vertical-bar
 * guidance, iphone-duo.md §3.3 / §6). Everywhere else the answer is `{ edge: null, width: 0 }` and
 * the shell lays out exactly as it does today.
 *
 * WHAT THIS DELIBERATELY IS NOT: a pose test. Nothing here asks whether the device folds, what the
 * hinge angle is, or which display we are on — only whether an edge has been named, and what room
 * there is on it. That is the whole of Apple's rule ("adapt to reserved regions, never detect the
 * device"). A future non-foldable iPhone with a side strip gets the right layout for free; a Duo
 * INNER display with nothing docked, an iPad and a classic iPhone in landscape all correctly get
 * `null` (the landscape insets are symmetric — see deriveVerticalBarEdge).
 *
 * WHERE `width` COMES FROM, and why this file no longer works it out itself: platform/foldState.ts
 * `resolveVerticalBar()`. Usually it is the measured inset on the docked edge — the strip is
 * Apple's and its width is theirs to change. In a Split View PANE (20260919, measured) there IS no
 * reserved strip: the insets are honestly 0/0/0/0 while the native `verticalBarEdge` trait still
 * names an edge, and the answer is a rail of our own at SYNTHESIZED_VERTICAL_BAR_WIDTH — gated on
 * the pane, because the same trait may name an edge in a full-screen session nobody has measured.
 * That one decision lives in foldState.ts and NOT here because the store's `--safe-*` publisher has
 * to make exactly the same call — the dock and the content beside it must inset by the same number
 * or the column lands on top of the page.
 *
 * 20260920 - AND THE SAME CALL ANSWERS A SECOND QUESTION, `reserved`: was that width the SYSTEM's
 * (an inset we sit inside) or OURS (a rail carved from pane width)? It rides along because the
 * stylesheet's camera-housing reserve is owed in the first case and pure dead space in the second
 * — see the field below. One decision, read from both sides; nothing downstream re-derives it.
 *
 * 20260920 (same day, after the owner tested it) - AND `reserved` TURNED OUT TO BE THE WRONG
 * PREDICATE FOR THE CLEARANCE, though it is still the right one for the width. The clearance is
 * owed at the end where the system's furniture actually IS — the head in a closed pose, the head
 * again in a Split View pane (no camera there, but the OS paints its status circle at the head of
 * the rail), and neither end when 27.1 reports both clear. So the snapshot carries a third field,
 * `housing`, from resolveHousingEnds(); `reserved` stays because it is that resolver's fallback
 * when no host could report an end.
 */

import { useSyncExternalStore } from 'react';
import { useFoldStateStore, resolveFoldState } from '@/platform/foldStateStore.ts';
import {
  OCCLUSION_CLEARANCE_GAP,
  resolveHousingEnds,
  resolveVerticalBar,
  type FoldState,
  type VerticalBarEdge,
} from '@/platform/foldState.ts';

/** Which END of the docked column owes a clearance for the system's own furniture. One value rather
 *  than two booleans because it becomes ONE DOM attribute (data-side-dock-housing) that the
 *  stylesheet keys four rules off — the same currency as `edge`, and for the same reason: the
 *  component reports the fact, the stylesheet spends it. */
export type SideDockHousing = 'head' | 'foot' | 'both' | 'none';

export interface SideDock {
  /** Which edge the system reserved, or null when no side is reserved. */
  edge: VerticalBarEdge | null;
  /** CSS px available on that edge: the measured inset where the system reserved one, the
   *  synthesized rail where it named an edge and reserved nothing. 0 when `edge` is null, and never
   *  between 0 and MIN_VERTICAL_BAR_INSET: a strip too narrow to hold a control reports no edge at
   *  all rather than a dock that would hang off the window. See resolveVerticalBar. */
  width: number;
  /**
   * 20260920 - WHOSE STRIP IS IT? True when the SYSTEM reserved it (we are inside Apple's
   * furniture — its status bar, its vertical-bar buttons and, in the closed pose, the camera
   * housing are in the column with us); false when we carved the rail ourselves out of ordinary
   * pane width, where none of that is in it.
   *
   * A THIRD FIELD RATHER THAN A DERIVATION, because the consumer with geometry riding on it is a
   * STYLESHEET. SideDock.module.css reserves room for the camera housing at BOTH ends of the
   * column — right in the closed pose, where the housing is in the strip at an end the insets
   * cannot name, and pure waste in a Split View pane, where the strip is ours and there is no
   * housing in it at all. The owner saw the waste as ~96px of dead space under the hamburger
   * (20260920). The dock reads this off its own DOM attribute; nothing re-derives the branch, and
   * nothing asks `splitView` a furniture question.
   *
   * FALSE when `edge` is null: no strip, so no owner. Do not read it as "synthesized" without
   * checking `edge` first.
   *
   * 20260920 - AND IT IS NO LONGER THE FINAL WORD ON THE CLEARANCE, only an input to it: see
   * `housing` below, and resolveHousingEnds() for why the question moved.
   */
  reserved: boolean;
  /**
   * 20260920 - WHICH END OF THE COLUMN HAS SYSTEM FURNITURE IN IT.
   *
   * The clearance used to be paid at both ends of a system strip and at neither end of a carved
   * rail, and the owner reported both halves wrong on the same day: 96px of nothing under the
   * bottom buttons in the closed pose (the camera is at the HEAD — measured), and a toggle sitting
   * too high in a Split View pane (which has no camera but DOES get the OS status circle at the
   * head of the rail). platform/foldState.ts resolveHousingEnds() decides it, from the occlusion
   * ends Unity now pushes, falling back on `reserved` when nothing was reported.
   *
   * 'none' when `edge` is null — no column, nothing to clear.
   */
  housing: SideDockHousing;
  /**
   * 20260926 - THE HEAD'S CLEARANCE WHEN THE SYSTEM MEASURED HOW DEEP ITS ZONE IS: the occlusion
   * extent + OCCLUSION_CLEARANCE_GAP (24), in CSS px. The closed portrait pose's status zone is 170
   * deep, so a fixed 96 put the head's first control inside it; with the reading the head clears
   * 194. Present only where resolveHousingEnds() returned an extent for an occupied head — absent
   * everywhere else, and the stylesheet's 96px allowance (--side-dock-housing-reserve) answers
   * then, exactly as before. Handed to <SideDock>, which publishes it as a custom property; the
   * stylesheet still decides which end pays (data-side-dock-housing).
   */
  headClearance?: number;
  /** 20260926 - the same, for the foot (82 + 24 = 106 at the housing end of a closed landscape). */
  footClearance?: number;
}

const NO_DOCK: SideDock = { edge: null, width: 0, reserved: false, housing: 'none' };

// Memoised on the resolved FoldState's identity for the same reason resolveFoldState memoises on
// its inputs: useSyncExternalStore demands a reference-stable snapshot, and resolveFoldState
// already returns the same object whenever nothing has changed.
let memo: { from: FoldState; out: SideDock } | null = null;

/** Non-subscribing read, for the places that need the answer once — an event handler, a measurement
 *  pass, a module that is not a component. */
export function getSideDockSnapshot(): SideDock {
  const fold = resolveFoldState(useFoldStateStore.getState());
  if (memo !== null && memo.from === fold) return memo.out;

  const edge = fold.verticalBarEdge;
  // 20260919 - THE EDGE AND THE ROOM ON IT ARE TWO DIFFERENT QUESTIONS, and only a PUSHED state can
  // make them disagree: deriveVerticalBarEdge cannot name an edge the insets do not show, but
  // Unity's native `verticalBarEdge` trait can — it "reflects the system's preferred edge
  // regardless of whether a vertical bar is currently visible", which is exactly the pose a pane
  // produces. (20260920 - it no longer OUTRANKS the insets, which this comment used to say it did:
  // a measured asymmetric pair names the side, and the trait answers only where four numbers
  // cannot. platform/foldState.ts resolveVerticalBarEdge carries the defect that forced it.)
  // resolveVerticalBar() is where the
  // three cases are decided, in one place, for this hook and the store's `--safe-*` publisher
  // alike — and it is also where `reserved` comes from, which is the same decision read from its
  // other side: an inset we sit INSIDE is the system's strip, a rail we carve is not.
  //
  //   • a usable measured inset → that inset (the Duo outer display's reserved strip);
  //   • an inset of ZERO, IN A PANE → SYNTHESIZED_VERTICAL_BAR_WIDTH, because the system named an
  //     edge and reserved nothing, so the rail is ours to carve. THIS IS NOW THE MAIN PATH, not a
  //     curiosity: it is every Split View pane on the inner display, where the owner measured
  //     0/0/0/0 in both pane positions on 20260919. (This comment used to say the disagreement was
  //     "unreachable today". It was written before that measurement, and it was wrong the moment
  //     Unity started reporting the trait.)
  //   • an inset of ZERO with NO pane reported → 0, i.e. no dock. The trait names the system's
  //     preferred edge "regardless of whether a vertical bar is currently visible", so a
  //     full-screen inner-display session may produce this shape too and nobody has measured
  //     whether it does. foldState.ts carries the full reasoning and the condition for lifting it.
  //   • a non-zero inset UNDER MIN_VERTICAL_BAR_INSET → 0, i.e. no dock. The system reserved that
  //     strip and said how wide; it is not ours to widen, and we cannot use it as it stands,
  //     because this column is pinned flush to the window edge — a 12px strip pushes most of each
  //     control PAST that edge, where the viewport clips it (not the symmetric overhang the
  //     stylesheet's "visible beats invisible" note assumes, which only holds inboard). Falling
  //     back to the header is the answer that always fits.
  //
  // So the dock is refused on a 0 width, which covers both "no edge at all" and "an edge we cannot
  // honour", and nothing here has to know which.
  const bar = resolveVerticalBar(fold);
  // 20260920 - and the third answer off the same state: which END of the column owes a clearance.
  // resolveHousingEnds() calls resolveVerticalBar() itself, so the two can never disagree about
  // whether there is a dock at all; the branch below still refuses on width, which is the older and
  // narrower test of the two.
  const ends = resolveHousingEnds(fold);
  const housing: SideDockHousing = ends.head
    ? (ends.foot ? 'both' : 'head')
    : (ends.foot ? 'foot' : 'none');
  let out: SideDock = NO_DOCK;
  if (edge !== null && bar.width !== 0) {
    out = { edge, width: bar.width, reserved: bar.reserved, housing };
    // 20260926 - the per-end clearance from a MEASURED extent. Keys added only when known, so a
    // pose with no reading resolves to exactly the snapshot it always did (and the stylesheet's
    // 96px fallback answers).
    if (ends.headExtent !== undefined) out.headClearance = ends.headExtent + OCCLUSION_CLEARANCE_GAP;
    if (ends.footExtent !== undefined) out.footClearance = ends.footExtent + OCCLUSION_CLEARANCE_GAP;
  }

  memo = { from: fold, out };
  return out;
}

function subscribe(onStoreChange: () => void): () => void {
  return useFoldStateStore.subscribe(onStoreChange);
}

export function useSideDock(): SideDock {
  return useSyncExternalStore(subscribe, getSideDockSnapshot, getSideDockSnapshot);
}
